import apiService from './api';

// Helper function to check if localStorage is available and working
function isStorageAvailable() {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.error('localStorage is not available:', e);
    return false;
  }
}

export interface LoginCredentials {
  // Admin signs in with mobile phone number (preferred) or email
  identifier: string;
  password: string;
}

export interface OtpVerification {
  identifier: string;
  otpCode: string;
}

export interface PasswordReset {
  token: string;
  newPassword: string;
}

export interface TwoFactorSetup {
  userId: string;
}

export interface TwoFactorVerify {
  userId: string;
  token: string;
}

export interface AdminRegistration {
  name: string;
  email: string;
  password: string;
  isSuperAdmin: boolean;
}

class AuthService {
  // Admin Login - Step 1
  public async adminLogin(credentials: LoginCredentials) {
    console.log('AuthService: Calling admin login endpoint');

    try {
      // ارسال مستقیم درخواست لاگین بدون بررسی‌های اضافی
      const response = await apiService.post('/admin/auth/login', credentials);
      console.log('AuthService: Login response received');
      return response;
    } catch (error) {
      console.error('AuthService: Login error');
      throw error;
    }
  }

  // Admin Login - Step 2 (Verify OTP)
  public async verifyOtp(data: OtpVerification) {
    console.log('AuthService: Verifying OTP');

    try {
      // ارسال مستقیم درخواست تایید OTP بدون بررسی‌های اضافی
      const response = await apiService.post('/admin/auth/verify-otp', data);
      console.log('AuthService: OTP verification response received');
      return response;
    } catch (error) {
      console.error('AuthService: OTP verification error');
      throw error;
    }
  }

  // Admin Resend OTP
  public async resendOtp(identifier: string) {
    console.log('AuthService: Resending OTP');
    try {
      const response = await apiService.post('/admin/auth/resend-otp', { identifier });
      console.log('AuthService: Resend OTP response received:', response);
      return response;
    } catch (error) {
      console.error('AuthService: Resend OTP error:', error);
      throw error;
    }
  }

  // Admin Register
  public async registerAdmin(data: AdminRegistration) {
    return apiService.post('/admin/auth/register', data);
  }

  // Admin Refresh Token
  public async refreshToken(refreshToken: string) {
    return apiService.post('/admin/auth/refresh-token', { refreshToken });
  }

  // Admin Forgot Password
  public async forgotPassword(email: string) {
    return apiService.post('/admin/auth/forgot-password', { email });
  }

  // Admin Reset Password
  public async resetPassword(data: PasswordReset) {
    return apiService.post('/admin/auth/reset-password', data);
  }

  // Admin Logout
  public async logout() {
    try {
      const result = await apiService.post('/admin/auth/logout');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return result;
    } catch (error) {
      console.error('Logout error:', error);
      // حتی در صورت خطا، توکن‌ها را پاک کنیم
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      throw error;
    }
  }

  // Admin 2FA operations
  public async enableTwoFactor(data: TwoFactorSetup) {
    return apiService.post('/auth/admin/2fa/enable', data);
  }

  public async verifyTwoFactor(data: TwoFactorVerify) {
    return apiService.post('/auth/admin/2fa/verify', data);
  }

  public async disableTwoFactor(data: TwoFactorVerify) {
    return apiService.post('/auth/admin/2fa/disable', data);
  }

  // Check if user is authenticated
  public isAuthenticated(): boolean {
    try {
      return !!localStorage.getItem('accessToken');
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  }

  // Get current user data
  public getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }
}

export default new AuthService(); 